import React from 'react'
import { ArrowLeftIcon, ChevronRightIcon, CloseIcon, WarningIcon } from './icons'
import styles from './SettingsUi.module.css'

export const SettingsPanel = ({ children, onClose }: { children: React.ReactNode, onClose?: () => void }) => (
  <div
    className={styles.overlay}
    onClick={(e) => {
      if (e.target === e.currentTarget) onClose?.()
    }}
  >
    {/* Panel + close button share a row so the button hangs off the panel's
        top-right corner instead of the viewport's. */}
    <div
      className={styles.dialog}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.()
      }}
    >
      <div className={styles.panel}>{children}</div>
      {onClose && (
        <button type='button' className={styles.closeButton} onClick={onClose} title='Close'>
          <CloseIcon size={16} />
        </button>
      )}
    </div>
  </div>
)

/**
 * Screen layout inside the panel: a fixed header block and a scrolling body.
 * The panel itself never scrolls, so the back button, the title and the
 * splitter under them stay in place regardless of how long the body is.
 */
export const SettingsScreen = ({ title, onBack, children }: {
  title: string
  onBack?: () => void
  children: React.ReactNode
}) => <>
  <div className={styles.header}>
    {onBack && (
      <button type='button' className={styles.backButton} onClick={onBack}>
        <ArrowLeftIcon size={16} />
        Назад
      </button>
    )}
    <h1 className={styles.title}>{title}</h1>
  </div>
  <div className={styles.body}>{children}</div>
</>

export const SettingsDivider = () => <hr className={styles.divider} />

/** Titled group of rows: 24px around the group, 12px between its rows. */
export const SettingsSection = ({ title, children }: { title: string, children: React.ReactNode }) => (
  <div className={styles.section}>
    <div className={styles.sectionTitle}>{title}</div>
    {children}
  </div>
)

export interface SegmentOption {
  value: string
  label: string
  icon?: React.ReactNode
  /** Draws the pill's hairline splitter before this option (FPS: … | Авто). */
  splitBefore?: boolean
}

export const SegmentedField = ({ title, options, value, onChange, disabled }: {
  title: string
  options: readonly SegmentOption[]
  value: string
  onChange: (value: string) => void
  disabled?: boolean
}) => (
  <div className={styles.field}>
    <div className={styles.fieldTitle}>{title}</div>
    <div className={styles.segmentedRow}>
      {options.map(option => <React.Fragment key={option.value}>
        {option.splitBefore && <span className={styles.segmentSplitter} />}
        <button
          type='button'
          disabled={disabled}
          className={`${styles.segment} ${styles.segmentWide} ${option.value === value ? styles.segmentActive : ''}`}
          onClick={() => onChange(option.value)}
        >
          {option.icon}
          {option.label}
        </button>
      </React.Fragment>)}
    </div>
  </div>
)

/** Warning/info block: icon, heading, body copy, optional dismiss button. */
export const NoticeCard = ({ title, children, onDismiss }: {
  title: string
  children: React.ReactNode
  onDismiss?: () => void
}) => (
  <div className={styles.notice}>
    <div className={styles.noticeBody}>
      <span className={styles.noticeIcon}><WarningIcon size={20} /></span>
      <div className={styles.noticeText}>
        <div className={styles.noticeTitle}>{title}</div>
        <p className={styles.noticeDescription}>{children}</p>
      </div>
    </div>
    {onDismiss && (
      <button type='button' className={styles.noticeDismiss} onClick={onDismiss} title='Скрыть'>
        <CloseIcon size={16} />
      </button>
    )}
  </div>
)

export const ToggleRow = ({ icon, label, description, checked, onChange, disabled }: {
  icon?: React.ReactNode
  label: string
  description?: string
  checked: boolean
  onChange: (value: boolean) => void
  disabled?: boolean
}) => (
  <button
    type='button'
    className={styles.switchRow}
    disabled={disabled}
    aria-pressed={checked}
    onClick={() => onChange(!checked)}
  >
    <span className={`${styles.switch} ${checked ? styles.switchOn : ''}`}>
      <span className={styles.switchKnob} />
    </span>
    <span className={styles.switchText}>
      <span className={styles.switchTitle}>
        {icon && <span className={styles.switchIcon}>{icon}</span>}
        <span className={styles.switchLabel}>{label}</span>
      </span>
      {description && <span className={styles.switchDescription}>{description}</span>}
    </span>
  </button>
)

export const NavCards = ({ children }: { children: React.ReactNode }) => (
  <div className={styles.cards}>{children}</div>
)

export const NavCard = ({ label, onClick, wide }: { label: string, onClick: () => void, wide?: boolean }) => (
  <button type='button' className={`${styles.card} ${wide ? styles.cardWide : ''}`} onClick={onClick}>
    {label}
    <span className={styles.cardChevron}><ChevronRightIcon size={20} /></span>
  </button>
)

